import React, {useEffect, useState} from "react"
import {Link} from "react-router-dom"
import {Field} from "formik"
import * as Yup from "yup"

import getAppConfig from "~/utils/getAppConfig"
import useBookingAvailableDates from "~/utils/useBookingAvailableDates"

import {apiAreas, apiClients, apiPacks} from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"

import CatchCard from "~/library/CatchCard"
import LoadingWrapper from "~/library/LoadingWrapper"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikTextarea from "~/library/FormikTextarea"
import FormikRadioGroup from "~/library/FormikRadioGroup"
import CurrencyInput from "~/library/CurrencyInput"
import DateSelector from "~/library/DateSelector"
import SelectOrCreate from "~/library/SelectOrCreate"

import type Area from "~/types/Area"
import type Pack from "~/types/Pack"
import type Booking from "~/types/Booking"

const bookingSchema = Yup.object({
  areaId: Yup.string().required("An area selection is required"),
  date: Yup.string().required("A booking date is required"),
  name: Yup.string().required("A booking name is required"),
  details: Yup.string().nullable(),
  packIds: Yup.array().of(Yup.string()).optional(),
})

interface BookingFormProps {
  onSubmit: (values: any) => void
  onCancel?: () => void
  initialValues?: Booking | null | any
  isEditing?: boolean
}

const BookingForm: React.FC<BookingFormProps> = ({
    onSubmit,
    onCancel,
    initialValues,
    isEditing = false,
  }) => {
  const {venueSlug} = getAppConfig()

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [availability, setAvailability] = useState<Area[]>([])
  const [isBookedAreasLoading, setIsBookedAreasLoading] = useState(false)

  const {enabled, loading, activeDates} = useBookingAvailableDates()

  const clientsEndpoint = apiClients.index.path({venue_slug: venueSlug})
  const packsEndpoint = apiPacks.index.path({venue_slug: venueSlug})

  const {data: packs} = useFetchResource<{ packs: Pack[] }, "packs">(packsEndpoint, "packs", {
    scope: "active",
  })

  useEffect(() => {
    if (initialValues?.date) setSelectedDate(initialValues.date)
  }, [initialValues, isEditing])

  useEffect(() => {
    if (!selectedDate) return

    setIsBookedAreasLoading(true)
    const req = apiAreas.availability({
      venue_slug: venueSlug,
      query: {date: selectedDate},
    })

    req.then(({areas}) => setAvailability(areas))
    req.finally(() => setIsBookedAreasLoading(false))
  }, [venueSlug, selectedDate])

  const defaultValues = {
    name: "",
    clientId: "",
    clientAttributes: {
      name: "",
      email: "",
      phone: "",
    },
    areaId: "",
    anticipatedRevenueCents: 0,
    date: null,
    packIds: [],
  }

  const handlePackChange = (packId: string, field: any) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentValues = Array.from(new Set((field.value || []).filter(Boolean)))

      if (e.target.checked) {
        field.onChange({
          target: {
            name: field.name,
            value: [...currentValues, packId],
          },
        })
      } else {
        field.onChange({
          target: {
            name: field.name,
            value: currentValues.filter((id: string) => id !== packId),
          },
        })
      }
    }
  }

  const formInitialValues =
    isEditing && initialValues
      ? {
        ...initialValues,
        packIds: initialValues.packs?.map((pack: Pack) => pack.id).filter(Boolean) || [],
        areaId: initialValues.areaId || initialValues.area?.id || "",
      }
      : {
        ...defaultValues,
        ...(initialValues || {}),
      }

  return (
    <FormikWrapper
      onSubmit={onSubmit}
      validationSchema={bookingSchema}
      onValuesChange={({date}: any) => setSelectedDate(date)}
      initialValues={formInitialValues}
    >
      <h4>Booking Details</h4>
      <CatchCard>
        <FormikInput
          name="name"
          label="Booking Name"
          placeholder="Woodworking Enthusiasts Outing"
          required
          helpText=""
        />

        <CurrencyInput
          name="anticipatedRevenueCents"
          label="Manual Subtotal"
          placeholder="8,6753.09"
          currency="USD"
        />

        <div className="small text-muted mb-3">
          Optional team-side manual base amount. Venue surcharge and tax still apply through booking totals.
        </div>
        <FormikTextarea
          name="details"
          label="Additional Details"
          placeholder="Client likes scotch, hates computers"
          helpText=""
          maxLength={1000}
        />
      </CatchCard>

      <h4>Client Details</h4>
      <CatchCard>
        {isEditing && initialValues?.client && (
          <>
            <p className="small">To update client details go to the clients page</p>
            <div className="field">
              <label>Name</label>
              <p className="form-control disabled"> {initialValues.client.name} </p>
            </div>
            <div className="field">
              <label>Email</label>
              <p className="form-control disabled"> {initialValues.client.email} </p>
            </div>
            <div className="field">
              <label>Phone</label>
              <p className="form-control disabled"> {initialValues.client.phone} </p>
            </div>
            <p className="mt-3">Change Client</p>
          </>
        )}

        <SelectOrCreate
          name="clientId"
          attributesPrefix="clientAttributes"
          placeholder="Search or Create a Client"
          endpoint={clientsEndpoint}
          responseKey="clients"
          labelKey="name"
          searchParam="name_or_email_cont"
          allowCreate={!isEditing}
          createFields={[
            {name: "name", label: "Name", type: "text", required: true},
            {name: "email", label: "Email", type: "email", required: true},
            {name: "phone", label: "Phone", type: "text"},
          ]}
        />
      </CatchCard>

      <h4>Select Packs</h4>
      <CatchCard>
        <LoadingWrapper isLoading={!packs}>
          <Field name="packIds">
            {({field}: any) => (
              <div>
                {packs && packs.length > 0 ? (
                  packs.map((pack: Pack) => {
                    const packId = pack.id
                    const isSelected = field.value?.includes(packId)

                    return (
                      <label
                        key={packId}
                        htmlFor={`pack-${packId}`}
                        className={`
                          card border-2 mb-3 cursor-pointer
                          ${isSelected ? "border-primary" : "border-gray"}
                          transition-all
                        `}
                      >
                        <div className="card-body p-3">
                          <input
                            type="checkbox"
                            id={`pack-${packId}`}
                            value={packId}
                            checked={isSelected}
                            onChange={handlePackChange(packId, field)}
                            className="form-check-input d-none"
                          />

                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h5 className="card-title mb-0">{pack.name}</h5>
                            <span className="text-muted">
                              ${(pack.priceCents / 100).toFixed(2)}
                            </span>
                          </div>

                          {pack.description && (
                            <p className="text-muted mb-1" style={{fontSize: 13}}>
                              {pack.description}
                            </p>
                          )}

                          {pack.items && pack.items.length > 0 && (
                            <div className="text-muted" style={{fontSize: 13}}>
                              {pack.items.map((item: any, index: number) => (
                                <span key={item.id}>
                                  {item.quantity > 1 && `${item.quantity}x `}
                                  {item.lineItem?.name || "Item"}
                                  {index < pack.items.length - 1 && ", "}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </label>
                    )
                  })
                ) : (
                  <div className="text-muted text-center p-4">No packs available</div>
                )}
              </div>
            )}
          </Field>
        </LoadingWrapper>
      </CatchCard>

      <div>
        <h4>Select a Date</h4>
        <DateSelector
          name="date"
          isFormik
          futureOnly
          disabled={isBookedAreasLoading || (enabled && loading)}
          activeDates={enabled ? activeDates : undefined}
        />
      </div>

      <h4>Area</h4>
      <CatchCard>
        <LoadingWrapper isLoading={isBookedAreasLoading}>
          <div className="row"></div>
          <FormikRadioGroup
            name="areaId"
            variant="card"
            options={availability.map((a: any) => {
              const isBooked = a.isBooked && a.id != initialValues?.areaId
              return {
                value: a.id,
                label: `${a.name}${isBooked ? " (Booked)" : ""}`,
                disabled: isBooked,
                description: `Capacity: ${a.capacity}`,
              }
            })}
            emptyState={
              <div className="d-flex align-items-center justify-content-center">
                Date must be selected
              </div>
            }
          />
        </LoadingWrapper>
      </CatchCard>

      <div className="d-flex">
        <div className="ms-auto">
          {onCancel ? (
            <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
              Cancel
            </button>
          ) : (
            <Link className="btn btn-secondary me-2" to="/bookings">
              Cancel
            </Link>
          )}
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </div>
    </FormikWrapper>
  )
}

export default BookingForm
